export type BusinessType = {
  id: string;
  label: string;
  icon: string;
  aiName: string;
  greeting: string;
  tone: string;
  transferWhen: string;
  businessHours: string;
  knowledgeStarter: string;
};

export const BUSINESS_TYPES: BusinessType[] = [
  {
    id: "medical",
    label: "Medical / Healthcare",
    icon: "ri-hospital-line",
    aiName: "Aria",
    greeting:
      "Thanks for calling. This is Aria, your virtual healthcare assistant. How can I help you today?",
    tone: "professional",
    transferWhen:
      "Patient describes an emergency, requests to speak with a doctor or nurse, or asks about a specific diagnosis or medication.",
    businessHours: "Monday–Friday 8am–5pm",
    knowledgeStarter:
      "We are a primary care practice committed to compassionate, evidence-based care. Our team handles routine check-ups, chronic disease management, and referrals to specialists.",
  },
  {
    id: "dental",
    label: "Dental",
    icon: "ri-hospital-line",
    aiName: "Sophie",
    greeting:
      "Thanks for calling! This is Sophie with our dental office. Are you looking to schedule an appointment or do you have a question for us?",
    tone: "professional",
    transferWhen:
      "Patient reports severe tooth pain or a dental emergency, or needs to discuss a treatment plan or billing in detail.",
    businessHours: "Monday–Friday 8am–5pm, Saturday 9am–2pm",
    knowledgeStarter:
      "We offer comprehensive dental care including cleanings, fillings, crowns, and cosmetic services. New patients are always welcome and we accept most major insurance plans.",
  },
  {
    id: "legal",
    label: "Legal / Law Firm",
    icon: "ri-scales-3-line",
    aiName: "Alex",
    greeting:
      "Thanks for calling. You've reached the legal offices — this is Alex. How may I direct your call today?",
    tone: "professional",
    transferWhen:
      "Caller describes an urgent legal matter, requests to speak with an attorney, or is an existing client with a case-specific question.",
    businessHours: "Monday–Friday 9am–6pm",
    knowledgeStarter:
      "We are a full-service law firm providing counsel in personal injury, family law, and business litigation. Initial consultations are confidential and available by appointment.",
  },
  {
    id: "insurance",
    label: "Insurance Agency",
    icon: "ri-shield-check-line",
    aiName: "Jordan",
    greeting:
      "Thanks for calling. This is Jordan with our insurance agency. Are you calling about an existing policy or looking for a new quote?",
    tone: "professional",
    transferWhen:
      "Caller wants to file a claim, dispute a policy decision, or requires a licensed agent to discuss coverage specifics.",
    businessHours: "Monday–Friday 8:30am–5:30pm",
    knowledgeStarter:
      "We offer auto, home, life, and commercial insurance through multiple top-rated carriers. Our agents shop the market to find the best rate and coverage for your needs.",
  },
  {
    id: "real_estate",
    label: "Real Estate",
    icon: "ri-building-2-line",
    aiName: "Morgan",
    greeting:
      "Thanks for calling! This is Morgan with our real estate team. Are you looking to buy, sell, or just have a question about the market?",
    tone: "professional",
    transferWhen:
      "Caller wants to schedule a showing, make an offer, or discuss a specific property listing in detail.",
    businessHours: "Monday–Saturday 9am–7pm",
    knowledgeStarter:
      "We are a full-service real estate brokerage helping buyers and sellers across the local market. Our agents provide personalized guidance from the first showing through closing day.",
  },
  {
    id: "hvac",
    label: "HVAC / Home Services",
    icon: "ri-tools-line",
    aiName: "Max",
    greeting:
      "Thanks for calling. This is Max — what's going on with your system today?",
    tone: "direct",
    transferWhen:
      "Caller reports a complete system failure, carbon monoxide concern, or needs same-day emergency service.",
    businessHours: "Monday–Friday 7am–6pm, Saturday 8am–2pm",
    knowledgeStarter:
      "We service, repair, and install heating, cooling, and ventilation systems for residential and light commercial properties. Emergency service is available 24/7 for existing customers.",
  },
  {
    id: "salon",
    label: "Salon / Beauty",
    icon: "ri-scissors-cut-line",
    aiName: "Bella",
    greeting:
      "Thanks for calling! This is Bella at the salon — would you like to book an appointment or do you have a question for us?",
    tone: "friendly",
    transferWhen:
      "Caller has a complaint about a recent service or needs to speak with a stylist directly about a specific treatment.",
    businessHours: "Tuesday–Saturday 9am–7pm",
    knowledgeStarter:
      "We offer a full range of hair, nail, and skin services in a relaxing, welcoming environment. Walk-ins are welcome when stylists are available, and online booking is always open.",
  },
  {
    id: "restaurant",
    label: "Restaurant / Food",
    icon: "ri-restaurant-line",
    aiName: "Cleo",
    greeting:
      "Thanks for calling! This is Cleo — are you looking to place a to-go order or make a reservation?",
    tone: "friendly",
    transferWhen:
      "Caller has a complaint, a large-party reservation request, or a catering inquiry that needs a manager.",
    businessHours: "Monday–Sunday 11am–10pm",
    knowledgeStarter:
      "We serve fresh, made-to-order meals with dine-in, takeout, and delivery options available. Our menu changes seasonally and we can accommodate most dietary restrictions with advance notice.",
  },
  {
    id: "retail",
    label: "Retail",
    icon: "ri-store-2-line",
    aiName: "Riley",
    greeting:
      "Thanks for calling! This is Riley — happy to help. Are you checking on an order, or is there something else I can assist with?",
    tone: "friendly",
    transferWhen:
      "Caller reports a missing or damaged order, needs a supervisor to approve a return, or has a billing dispute.",
    businessHours: "Monday–Saturday 10am–8pm, Sunday 11am–6pm",
    knowledgeStarter:
      "We carry a curated selection of products and offer in-store pickup, local delivery, and shipping. Our return policy allows exchanges or store credit within 30 days of purchase.",
  },
  {
    id: "other",
    label: "Other Business",
    icon: "ri-briefcase-line",
    aiName: "Sam",
    greeting:
      "Thanks for calling! This is Sam — how can I help you today?",
    tone: "friendly",
    transferWhen:
      "Caller requests to speak with a team member directly or has an issue that requires human follow-up.",
    businessHours: "Monday–Friday 9am–5pm",
    knowledgeStarter:
      "We are committed to providing great service and will do our best to answer your questions or connect you with the right person on our team.",
  },
];

export function getDefaults(businessTypeId: string): BusinessType {
  return (
    BUSINESS_TYPES.find((b) => b.id === businessTypeId) ??
    // biome-ignore lint/style/noNonNullAssertion: "other" is always present
    BUSINESS_TYPES.find((b) => b.id === "other")!
  );
}
